import { getDefaultWrappers, allWrappers } from './wrappers';

/**
 * @typedef {Object} Wrapper
 * @property {string} contextKey - Unique identifier for the wrapper
 * @property {Function} getWrapper - Function that returns the wrapper implementation
 */

/**
 * @typedef {Object} HysteriCallOptions
 * @property {Object} [retry] - Retry configuration
 * @property {Object} [circuitBreaker] - Circuit breaker configuration
 * @property {Object} [timeout] - Timeout configuration
 * @property {Object} [abort] - Abort configuration
 * @property {Object} [fetch] - Fetch configuration
 * @property {Object} [newRelic] - New Relic metrics configuration
 */

/**
 * Validates the order of wrappers to ensure correct operation.
 * Enforces:
 * - Circuit breaker must come before retry
 * - Timeout must come before abort
 * - Terminal wrappers (fetch, contextTerminus) must be last
 * @param {Wrapper[]} wrappers - Array of wrappers to validate
 * @throws {Error} If wrapper order is invalid
 */
const validateWrapperOrder = (wrappers) => {
	const wrapperKeys = wrappers.map((w) => w.contextKey);
	const indexOf = (key) => wrapperKeys.indexOf(key);

	const circuitBreakerIndex = indexOf('circuitBreaker');
	const retryIndex = indexOf('retry');
	if (circuitBreakerIndex !== -1 && retryIndex !== -1) {
		if (circuitBreakerIndex > retryIndex) {
			throw new Error(
				'Invalid wrapper order: circuitBreaker must come before retry to properly track failure rates',
			);
		}
	}

	const timeoutIndex = indexOf('timeout');
	const abortIndex = indexOf('abort');
	if (timeoutIndex !== -1 && abortIndex !== -1) {
		if (timeoutIndex > abortIndex) {
			throw new Error(
				'Invalid wrapper order: timeout must come before abort so timeouts can trigger abort',
			);
		}
	}

	const newRelicIndex = indexOf('newRelic');
	if (newRelicIndex !== -1 && newRelicIndex !== 0) {
		throw new Error(
			'Invalid wrapper order: newRelic must be first to accurately measure total execution time',
		);
	}

	const terminalWrappers = ['fetch', 'contextTerminus'];
	const terminalIndices = terminalWrappers
		.map((key) => indexOf(key))
		.filter((i) => i !== -1);

	if (terminalIndices.length > 0) {
		const lastTerminalIndex = Math.max(...terminalIndices);
		if (lastTerminalIndex !== wrapperKeys.length - 1) {
			throw new Error(
				'Invalid wrapper order: terminal wrappers (fetch, contextTerminus) must be last in the chain',
			);
		}
	}
};

/**
 * Creates an execution context by merging base context with wrapper-specific options.
 * @param {Object} context - Base context containing at minimum { name: string }
 * @param {HysteriCallOptions} optionOverrides - Options to override for this execution
 * @param {Wrapper[]} wrappers - Array of wrappers to initialize context for
 * @returns {Object} Execution context with all wrapper configurations
 */
const createExecutionContext = (context, optionOverrides, wrappers) => {
	const executionContext = { ...context };
	wrappers.forEach((wrapper) => {
		const { contextKey } = wrapper;
		executionContext[contextKey] = { ...(context[contextKey] || {}) };
		executionContext[contextKey].options = {
			...(executionContext[contextKey].options || {}),
			...(optionOverrides[contextKey] || {}),
		};
	});
	return executionContext;
};

/**
 * @callback HysteriCallExecutor
 * @param {any[]} [fnArgs=[]] - Array of arguments to pass to the wrapped function
 * @param {HysteriCallOptions} [optionOverrides={}] - Options to override for this specific execution
 * @returns {Promise<any>} Promise that resolves with the function result or rejects with an error
 */

/**
 * @callback HysteriFetchExecutor
 * @param {string} fetchUrl - URL to fetch
 * @param {Object} [fetchOptions={}] - Fetch API options (method, headers, body, etc.)
 * @param {HysteriCallOptions} [optionOverrides={}] - Options to override for this specific execution
 * @returns {Promise<Response>} Promise that resolves with the fetch Response
 */

/**
 * Creates an isolated hysterics factory with independent service tracking.
 * Each factory maintains its own Set of called services for memory leak detection.
 * @returns {{hysteriCall: Function, hysteriFetch: Function}} Factory object with hysteriCall and hysteriFetch methods
 */
export const createHystericsFactory = () => {
	const hysteriCalledServices = new Set();

	/**
	 * Wraps a function with fault-tolerance capabilities (retry, circuit breaker, timeout, etc.).
	 * Returns an executor function that can be called multiple times with different arguments.
	 * @param {string} name - Unique name for this service (used for metrics and circuit breaker tracking)
	 * @param {Function|null} fn - Function to wrap, or null if using a terminal wrapper like fetch
	 * @param {HysteriCallOptions} [options={}] - Configuration options for all wrappers
	 * @param {Wrapper[]} [wrappers] - Array of wrappers to apply (defaults to standard set)
	 * @returns {HysteriCallExecutor} Async function that executes the wrapped function with fault tolerance
	 * @throws {Error} If wrapper order is invalid or if both fn and terminal wrapper are missing
	 */
	const hysteriCall = (
		name,
		fn,
		options = {},
		wrappers = getDefaultWrappers(),
	) => {
		if (!name || typeof name !== 'string') {
			throw new Error('hysteriCall requires a non-empty string name parameter');
		}
		validateWrapperOrder(wrappers);

		if (hysteriCalledServices.has(name)) {
			// eslint-disable-next-line no-console
			console.warn(
				`[hysterics] Warning: hysteriCall("${name}", ...) invoked multiple times. ` +
					'This can cause memory leaks and dilute circuit breaker statistics. ' +
					'Consider calling hysteriCall once per service at module scope.',
			);
		} else {
			hysteriCalledServices.add(name);
		}

		const hasTerminalWrapper = wrappers.some(
			(w) => w.contextKey === 'fetch' || w.contextKey === 'contextTerminus',
		);
		if (!fn && !hasTerminalWrapper) {
			throw new Error(
				'hysteriCall requires either a function or a terminal wrapper (fetch/contextTerminus) in the wrapper chain.',
			);
		}

		const context = createExecutionContext({ name }, options || {}, wrappers);

		let executionChain = fn;
		wrappers
			.slice()
			.reverse()
			.forEach((wrapper) => {
				executionChain = wrapper.getWrapper(executionChain, context);
			});

		return async (fnArgs = [], optionOverrides = {}) => {
			if (!Array.isArray(fnArgs)) {
				throw new Error('Function arguments must be provided as an array');
			}
			const executionContext = createExecutionContext(
				context,
				optionOverrides,
				wrappers,
			);
			return executionChain(fnArgs, executionContext);
		};
	};

	/**
	 * Creates a fault-tolerant fetch wrapper with retry, circuit breaker, and timeout capabilities.
	 * Automatically applies fetch-specific defaults for retry and circuit breaker error handling.
	 * @param {string} name - Unique name for this service (used for metrics and circuit breaker tracking)
	 * @param {HysteriCallOptions} [options={}] - Configuration options for all wrappers
	 * @returns {HysteriFetchExecutor} Async function that executes fetch with fault tolerance
	 */
	const hysteriFetch = (name, options = {}) => {
		if (!name || typeof name !== 'string') {
			throw new Error(
				'hysteriFetch requires a non-empty string name parameter',
			);
		}
		const callOptions = {
			...options,
			[allWrappers.retry.contextKey]: {
				isRetryable: allWrappers.fetch.defaultIsRetryable,
				...(options ? options[allWrappers.retry.contextKey] : {}),
			},
			[allWrappers.circuitBreaker.contextKey]: {
				errorFilter: allWrappers.fetch.defaultCircuitBreakerErrorFilter,
				...(options ? options[allWrappers.circuitBreaker.contextKey] : {}),
			},
		};

		const wrappers = [
			allWrappers.newRelic,
			allWrappers.duration,
			allWrappers.circuitBreaker,
			allWrappers.retry,
			allWrappers.timeout,
			allWrappers.abort,
			allWrappers.fetch,
		];

		const call = hysteriCall(name, null, callOptions, wrappers);

		return async (fetchUrl, fetchOptions = {}, optionOverrides = {}) => {
			const mergedOptions = {
				...optionOverrides,
				[allWrappers.fetch.contextKey]: {
					...(fetchOptions || {}),
					...(optionOverrides
						? optionOverrides[allWrappers.fetch.contextKey]
						: {}),
				},
			};
			return call([fetchUrl], mergedOptions);
		};
	};

	return {
		hysteriCall,
		hysteriFetch,
	};
};

const hystericsFactory = createHystericsFactory();

/**
 * Create a call wrapper that will invoke your supplied function with the supplied (or default) fault tolerance
 * wrappers (e.g. circuit breaker, retry).
 *
 * @param {String} name - Name of the service or action you are wrapping. Should be unique in your application.
 * @param {Function} fn - The function to invoke.
 * @param {Object} [options] - [Optional] Additional options to pass to wrappers, e.g. retry or circuit breaker options.
 * @param {Object[]} [wrappers] - [Optional] The intermediary wrappers to wrap your function with.
 *
 * @returns {HysteriCallExecutor} An async executor function ({@link HysteriCallExecutor}) that should be invoked
 *  instead of the original function whenever fault tolerant behavior is desired.
 */
export const { hysteriCall } = hystericsFactory;

/**
 * Create a call wrapper that will invoke `fetch` with the supplied (or default) fault tolerance wrappers
 * (e.g. circuit breaker, retry).
 *
 * @param {string} name - Name of the service or action you are wrapping. Should be unique in your application.
 * @param {Object} [options] - [Optional] Additional options to pass to wrappers, e.g. retry or circuit breaker options.
 * @param {Object[]} [wrappers] - [Optional] The intermediary wrappers to wrap your function with.
 *
 * @returns {HysteriFetchExecutor} An async executor function ({@link HysteriFetchExecutor}) that should be invoked
 *  instead of the `fetch` function whenever fault tolerant behavior is desired.
 */
export const { hysteriFetch } = hystericsFactory;
